import { Button, Combobox } from '@atlas/ui-primitives'
import { useEffect, useMemo, useState } from 'react'
import { useDialogFocusTrap } from '@/hooks/useDialogFocusTrap'

/**
 * Merge one taxonomy value into another: the duplicate is retired, and everything that
 * pointed at it — classifications, per-market names, Veeva ids — moves onto the survivor.
 *
 * A plain confirm would not do. The operation has a second argument the admin must choose
 * (which of the two survives), and it is directional in a way the copy has to make
 * unmissable: `Panacur 250mg` merged into `Panacur 250 mg` and the reverse leave different
 * codes standing, and only one of them is the one anyone will keep typing.
 */

export interface MergeCandidate {
	valueId: string
	code: string
	label: string
}

export interface MergeValueDialogProps {
	open: boolean
	/** The value being retired — the duplicate the admin started from. */
	subject: MergeCandidate | null
	/** Everything it could merge into: the same axis, minus itself. */
	candidates: MergeCandidate[]
	submitting?: boolean
	onConfirm: (intoValueId: string) => void
	onCancel: () => void
}

/** What the merge will do, once a survivor is chosen: the direction, and what is lost. */
function MergePreview({ subject, survivor }: { subject: MergeCandidate; survivor: MergeCandidate }) {
	return (
		<div className="mt-4 rounded-ds-control border border-ds-border bg-ds-surface p-3 text-ds-sm">
			<p className="text-ds-text">
				<span className="font-mono text-xs line-through">{subject.code}</span>
				<span className="mx-2 text-ds-text-muted">→</span>
				<span className="font-mono text-xs">{survivor.code}</span>
			</p>
			{/* Named plainly, because it is the part that cannot be undone: the merge moves
			    rows, it does not merge the two values' own descriptions. */}
			<p className="mt-2 text-ds-text-muted text-xs">
				“{subject.label}” stops existing. Anything written about it that the survivor does not already record — its own
				label, description and stored fields — is not carried across.
			</p>
		</div>
	)
}

export function MergeValueDialog({
	open,
	subject,
	candidates,
	submitting,
	onConfirm,
	onCancel,
}: MergeValueDialogProps) {
	const containerRef = useDialogFocusTrap(open)
	const [into, setInto] = useState('')
	const [search, setSearch] = useState('')

	// A fresh dialog starts with no target chosen: carrying the last one over is how the
	// wrong value gets merged into the wrong survivor.
	useEffect(() => {
		if (open) {
			setInto('')
			setSearch('')
		}
	}, [open])

	useEffect(() => {
		if (!open || submitting) return
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key !== 'Escape') return
			event.stopPropagation()
			onCancel()
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [open, submitting, onCancel])

	const options = useMemo(() => {
		const term = search.trim().toLowerCase()
		const eligible = candidates.filter((c) => c.valueId !== subject?.valueId)
		const matching = term
			? eligible.filter((c) => c.label.toLowerCase().includes(term) || c.code.toLowerCase().includes(term))
			: eligible
		// Capped, because an axis can hold thousands: the search box is how the rest are
		// reached, and a select of 4,000 options is not a chooser.
		return matching.slice(0, 50).map((c) => ({ value: c.valueId, label: `${c.label} — ${c.code}` }))
	}, [candidates, search, subject])

	const survivor = candidates.find((c) => c.valueId === into)

	if (!open || !subject) return null
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-text/40 p-4">
			<div
				ref={containerRef}
				tabIndex={-1}
				role="dialog"
				aria-modal="true"
				aria-labelledby="merge-dialog-title"
				className="w-full max-w-lg rounded-ds-modal bg-ds-surface-elevated p-6 shadow-ds-xl"
			>
				<h2
					id="merge-dialog-title"
					tabIndex={-1}
					data-autofocus
					className="font-ds-display font-semibold text-ds-text text-ds-xl leading-7 focus-visible:outline-none"
				>
					Merge “{subject.label}” into another value
				</h2>
				<p className="mt-2 text-ds-sm text-ds-text-muted leading-5">
					<span className="font-mono text-xs">{subject.code}</span> will be retired. Every asset classified with it, its
					market names and its Veeva ids move to the value you choose.
				</p>

				{/* The Combobox searches its own options, so the separate search box it used to
				    need is gone — one control to find the value and choose it.

				    `tabIndex={-1}` on the wrapper is load-bearing: the focus trap focuses the
				    first focusable control on open, and the Combobox opens its listbox on focus
				    (`menuTrigger="focus"`), so without somewhere else to land the dialog appears
				    with the options already covering its own buttons. The heading takes initial
				    focus instead, which is also what a screen reader should hear first. */}
				<div className="mt-4">
					<span className="mb-1 block text-ds-text-muted text-xs">Keep</span>
					<Combobox
						aria-label="Keep"
						value={into || null}
						onChange={(v) => setInto(v ?? '')}
						options={options}
						placeholder="Choose the value that survives"
					/>
				</div>

				{survivor ? <MergePreview subject={subject} survivor={survivor} /> : null}

				<div className="mt-6 flex justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						className="rounded-ds-full"
						isDisabled={submitting}
						onPress={onCancel}
					>
						Cancel
					</Button>
					<Button
						type="button"
						variant="danger"
						className="rounded-ds-full px-5"
						isDisabled={submitting || !into}
						onPress={() => into && onConfirm(into)}
					>
						{submitting ? 'Merging…' : 'Merge'}
					</Button>
				</div>
			</div>
		</div>
	)
}
